import {
  calculatePointAlongWallAxisFromRelativePosition,
  projectPointOntoWallAxisCartesianAware,
  projectRipOntoWall,
} from '@geometry'
import { ImmutableVector3 } from '@modugen/ui-toolkit'

export type RipLine = [ImmutableVector3, ImmutableVector3]

export const DEFAULT_RIP_CROSS_SECTION_SIZE = 0.15

export const DEFAULT_RIP_PREVIEW_CROSS_SECTION: CrossSectionTimberOrCLTOrSteel = {
  kind: 'Timber',
  shape: {
    kind_literal: 'RectangularCS',
    width: DEFAULT_RIP_CROSS_SECTION_SIZE,
    height: DEFAULT_RIP_CROSS_SECTION_SIZE,
  },
  material: { identifier: 'C24' } as SoftwoodMemberMaterial,
  usage_class: 'One',
}

export const buildCoordinateSystemForRipSegment = (
  start: ImmutableVector3,
  end: ImmutableVector3,
): CoordinateSystem => {
  const xDirection = start.directionTo(end)
  const yDirection = new ImmutableVector3(1, 0, 0)
  const zDirection = xDirection.cross(yDirection).normalize()

  return {
    origin: start,
    x_direction: xDirection,
    y_direction: yDirection,
    z_direction: zDirection,
    TOLERANCE: 0,
  }
}

export const parseRelativePosition = (value: unknown): number | undefined => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

export const computeRipPreviewLineFromPointOnWall = (
  wall: ShapeObject,
  point: Point,
): RipLine | null => {
  if (projectRipOntoWall(wall, point) === null) {
    return null
  }

  const pointVec = new ImmutableVector3(point.x, point.y, point.z)
  const projected = projectPointOntoWallAxisCartesianAware(wall, pointVec)
  const wallZ = wall.shape.points[0].z
  const highestZ = Math.max(...wall.shape.points.map(p => p.z))

  return [
    new ImmutableVector3(projected.x, projected.y, wallZ),
    new ImmutableVector3(projected.x, projected.y, highestZ),
  ]
}

export const ripPreviewLinesFromWallsAndPoint = (walls: ShapeObject[], point: Point): RipLine[] => {
  return walls
    .map(wall => computeRipPreviewLineFromPointOnWall(wall, point))
    .filter((line): line is RipLine => line !== null)
}

export const computeRipPreviewLineFromRelativePositionOnWall = (
  wall: ShapeObject,
  relativePosition: number,
): RipLine | null => {
  if (relativePosition < 0 || relativePosition > 1) {
    return null
  }

  const projected = calculatePointAlongWallAxisFromRelativePosition(wall, relativePosition)
  const wallZ = wall.shape.points[0].z
  const highestZ = Math.max(...wall.shape.points.map(p => p.z))

  return [
    new ImmutableVector3(projected.x, projected.y, wallZ),
    new ImmutableVector3(projected.x, projected.y, highestZ),
  ]
}

export const ripPreviewLinesFromWallsAndRelativePosition = (
  walls: ShapeObject[],
  relativePosition: number,
): RipLine[] => {
  return walls
    .map(wall => computeRipPreviewLineFromRelativePositionOnWall(wall, relativePosition))
    .filter((line): line is RipLine => line !== null)
}

export const isValidRipLine = (line: RipLine): boolean => {
  const [start, end] = line
  return (
    typeof start.x === 'number' &&
    typeof start.y === 'number' &&
    typeof start.z === 'number' &&
    typeof end.x === 'number' &&
    typeof end.y === 'number' &&
    typeof end.z === 'number'
  )
}
